import React, { useState } from "react";
import {
  TrendingUp,
  Target,
  Clock,
  RefreshCw,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { useApp } from "../../context/AppContext";
import { Card } from "../ui/Card";
import {
  DEFAULT_SCORING_CONFIG,
  calculateEngagement,
  getPathwayForScore,
  getMemberActivities,
} from "../../utils/engagementScoring";

const SCORING_STORAGE_KEY = "cmm_scoring_config";

// ── Scoring Configuration Panel ─────────────────────────────────

export const ScoringPanel = ({ palette }) => {
  const [config, setConfig] = useState(() => {
    try {
      const saved = localStorage.getItem(SCORING_STORAGE_KEY);
      return saved
        ? { ...DEFAULT_SCORING_CONFIG, ...JSON.parse(saved) }
        : { ...DEFAULT_SCORING_CONFIG };
    } catch {
      return { ...DEFAULT_SCORING_CONFIG };
    }
  });
  const [hasChanges, setHasChanges] = useState(false);
  const { showMessage } = useApp();

  const inputStyle = {
    padding: "6px 10px",
    borderRadius: 6,
    fontSize: FONT_SIZES.sm,
    fontFamily: FONT_FAMILY,
    border: `1px solid ${palette.inputBorder}`,
    background: palette.inputBg,
    color: palette.inputColor,
    outline: "none",
    width: 70,
    textAlign: "right",
  };

  const updatePoints = (type, value) => {
    const num = parseFloat(value);
    if (isNaN(num) || num < 0) return;
    setConfig((prev) => ({
      ...prev,
      contactPoints: { ...prev.contactPoints, [type]: num },
    }));
    setHasChanges(true);
  };

  const updateThreshold = (pathway, field, value) => {
    const num = parseInt(value);
    if (isNaN(num) || num < 0) return;
    setConfig((prev) => ({
      ...prev,
      pathways: {
        ...prev.pathways,
        [pathway]: { ...prev.pathways[pathway], [field]: num },
      },
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    try {
      localStorage.setItem(SCORING_STORAGE_KEY, JSON.stringify(config));
      setHasChanges(false);
      showMessage("Scoring configuration saved");
    } catch (err) {
      showMessage("Error saving configuration", "error");
    }
  };

  const handleReset = () => {
    setConfig({ ...DEFAULT_SCORING_CONFIG });
    localStorage.removeItem(SCORING_STORAGE_KEY);
    setHasChanges(false);
    showMessage("Scoring reset to defaults");
  };

  const contactTypeGroups = [
    {
      label: "Quick Contacts",
      types: [
        { id: "email", label: "Email reply", icon: "📧" },
        { id: "phone", label: "Phone call", icon: "📞" },
        { id: "text", label: "Text response", icon: "💬" },
      ],
    },
    {
      label: "Meetings",
      types: [
        { id: "coffee", label: "Coffee", icon: "☕" },
        { id: "lunch", label: "Lunch", icon: "🍽️" },
        { id: "meeting", label: "Other meeting", icon: "🤝" },
      ],
    },
    {
      label: "Commitment Signals",
      types: [
        { id: "donation", label: "Donation (any size)", icon: "💝" },
        { id: "volunteering", label: "Volunteering", icon: "🙋" },
        { id: "event", label: "Event attendance", icon: "🎪" },
      ],
    },
  ];

  return (
    <>
      {/* Point Values */}
      <Card style={{ marginBottom: 16 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 16,
            paddingBottom: 12,
            borderBottom: `1px solid ${palette.cardBorder}`,
          }}
        >
          <TrendingUp size={ICON_SIZES.md} style={{ color: palette.accent1 }} />
          <span
            style={{
              fontSize: FONT_SIZES.md,
              fontWeight: 600,
              color: palette.text1,
            }}
          >
            Engagement Point Values
          </span>
        </div>
        <p
          style={{
            fontSize: FONT_SIZES.xs,
            color: palette.text2,
            marginBottom: 16,
            lineHeight: 1.5,
          }}
        >
          Each logged contact earns points based on type. Points decay linearly
          to zero over {config.decayMonths} months. Scores are capped at{" "}
          {config.maxScore}.
        </p>
        {contactTypeGroups.map((group) => (
          <div key={group.label} style={{ marginBottom: 16 }}>
            <div
              style={{
                fontSize: FONT_SIZES.xs,
                fontWeight: 700,
                color: palette.text3,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                marginBottom: 8,
              }}
            >
              {group.label}
            </div>
            {group.types.map((type) => (
              <div
                key={type.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 0",
                  borderBottom: `1px solid ${palette.cardBorder}22`,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 16 }}>{type.icon}</span>
                  <span
                    style={{
                      fontSize: FONT_SIZES.sm,
                      color: palette.text1,
                      fontWeight: 500,
                    }}
                  >
                    {type.label}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={config.contactPoints[type.id] ?? 0}
                    onChange={(e) => updatePoints(type.id, e.target.value)}
                    style={inputStyle}
                  />
                  <span
                    style={{
                      fontSize: FONT_SIZES.xs,
                      color: palette.text3,
                      width: 24,
                    }}
                  >
                    pts
                  </span>
                </div>
              </div>
            ))}
          </div>
        ))}
      </Card>

      {/* Pathway Thresholds */}
      <Card style={{ marginBottom: 16 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 16,
            paddingBottom: 12,
            borderBottom: `1px solid ${palette.cardBorder}`,
          }}
        >
          <Target size={ICON_SIZES.md} style={{ color: palette.accent1 }} />
          <span
            style={{
              fontSize: FONT_SIZES.md,
              fontWeight: 600,
              color: palette.text1,
            }}
          >
            Pathway Thresholds
          </span>
        </div>
        <p
          style={{
            fontSize: FONT_SIZES.xs,
            color: palette.text2,
            marginBottom: 16,
            lineHeight: 1.5,
          }}
        >
          Donors are automatically placed in a pathway based on their
          engagement score.
        </p>
        {["cold", "connect", "contribute", "lead"].map((key) => {
          const pw = config.pathways[key];
          const colors = {
            cold: "#6b7280",
            connect: "#f59e0b",
            contribute: "#3b82f6",
            lead: "#10b981",
          };
          return (
            <div
              key={key}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 12px",
                marginBottom: 6,
                borderRadius: 8,
                background: `${colors[key]}08`,
                borderLeft: `4px solid ${colors[key]}`,
              }}
            >
              <span
                style={{
                  fontSize: FONT_SIZES.sm,
                  fontWeight: 700,
                  color: colors[key],
                  minWidth: 80,
                }}
              >
                {pw.label}
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input
                  type="number"
                  min="0"
                  value={pw.min}
                  onChange={(e) => updateThreshold(key, "min", e.target.value)}
                  style={inputStyle}
                />
                <span style={{ fontSize: FONT_SIZES.xs, color: palette.text3 }}>
                  to
                </span>
                <input
                  type="number"
                  min="0"
                  value={pw.max}
                  onChange={(e) => updateThreshold(key, "max", e.target.value)}
                  style={inputStyle}
                />
                <span
                  style={{
                    fontSize: FONT_SIZES.xs,
                    color: palette.text3,
                    width: 24,
                  }}
                >
                  pts
                </span>
              </div>
            </div>
          );
        })}
      </Card>

      {/* Decay & Cap */}
      <Card style={{ marginBottom: 16 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 16,
            paddingBottom: 12,
            borderBottom: `1px solid ${palette.cardBorder}`,
          }}
        >
          <Clock size={ICON_SIZES.md} style={{ color: palette.accent1 }} />
          <span
            style={{
              fontSize: FONT_SIZES.md,
              fontWeight: 600,
              color: palette.text1,
            }}
          >
            Decay & Limits
          </span>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 0",
            borderBottom: `1px solid ${palette.cardBorder}22`,
          }}
        >
          <div>
            <div
              style={{
                fontSize: FONT_SIZES.sm,
                color: palette.text1,
                fontWeight: 500,
              }}
            >
              Decay period
            </div>
            <div style={{ fontSize: FONT_SIZES.xs, color: palette.text3 }}>
              Contacts older than this earn zero points
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="number"
              min="1"
              value={config.decayMonths}
              onChange={(e) => {
                setConfig((prev) => ({
                  ...prev,
                  decayMonths: parseInt(e.target.value) || 24,
                }));
                setHasChanges(true);
              }}
              style={inputStyle}
            />
            <span style={{ fontSize: FONT_SIZES.xs, color: palette.text3 }}>
              months
            </span>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 0",
          }}
        >
          <div>
            <div
              style={{
                fontSize: FONT_SIZES.sm,
                color: palette.text1,
                fontWeight: 500,
              }}
            >
              Score cap
            </div>
            <div style={{ fontSize: FONT_SIZES.xs, color: palette.text3 }}>
              Maximum engagement score
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="number"
              min="1"
              value={config.maxScore}
              onChange={(e) => {
                setConfig((prev) => ({
                  ...prev,
                  maxScore: parseInt(e.target.value) || 150,
                }));
                setHasChanges(true);
              }}
              style={inputStyle}
            />
            <span style={{ fontSize: FONT_SIZES.xs, color: palette.text3 }}>
              pts
            </span>
          </div>
        </div>
      </Card>

      {/* Save / Reset */}
      <div style={{ display: "flex", gap: 12 }}>
        <button
          onClick={handleSave}
          disabled={!hasChanges}
          style={{
            flex: 1,
            padding: "10px 16px",
            borderRadius: 8,
            border: "none",
            background: hasChanges ? palette.accent1 : palette.cardBorder,
            color: "#fff",
            fontSize: FONT_SIZES.sm,
            fontWeight: 700,
            cursor: hasChanges ? "pointer" : "default",
            fontFamily: FONT_FAMILY,
            opacity: hasChanges ? 1 : 0.5,
          }}
        >
          Save Configuration
        </button>
        <button
          onClick={handleReset}
          style={{
            padding: "10px 16px",
            borderRadius: 8,
            border: `1px solid ${palette.cardBorder}`,
            background: "transparent",
            color: palette.text2,
            fontSize: FONT_SIZES.sm,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: FONT_FAMILY,
          }}
        >
          Reset to Defaults
        </button>
      </div>
    </>
  );
};

// ── Recalculate All Scores Panel ────────────────────────────────

const scoringKeyToAppKey = (key) => (key === "cold" ? "unengaged" : key);

export const RecalculatePanel = () => {
  const { palette, members, updateMember, showMessage, isAdmin, pathways, allGifts, allEventAttendance, allSponsorships } =
    useApp();

  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  if (!isAdmin) return null;

  const handleRecalculate = async () => {
    setRunning(true);
    setResult(null);

    try {
      let config = DEFAULT_SCORING_CONFIG;
      try {
        const saved = localStorage.getItem(SCORING_STORAGE_KEY);
        if (saved) config = { ...DEFAULT_SCORING_CONFIG, ...JSON.parse(saved) };
      } catch (e) {
        // Use defaults if localStorage config is corrupted
      }

      let updated = 0;
      let unchanged = 0;
      let skipped = 0;
      let errors = 0;
      const changes = [];

      for (const member of members) {
        try {
          if (member.pathway === "prospect") {
            skipped++;
            continue;
          }

          const engagement = calculateEngagement(member, config, getMemberActivities(member.id, allGifts, allEventAttendance, allSponsorships));
          const score = engagement.score;
          const rawResult = getPathwayForScore(score, config);
          const calculatedKey =
            typeof rawResult === "object" && rawResult !== null
              ? rawResult.key || rawResult.id || "cold"
              : rawResult;
          const appKey = scoringKeyToAppKey(calculatedKey);
          const currentPathway = member.pathway || "unengaged";

          if (currentPathway !== appKey) {
            await updateMember(member.id, { pathway: appKey }, true);
            changes.push({
              name: `${member.firstName} ${member.lastName}`,
              from: currentPathway,
              to: appKey,
              score: Math.round(score * 10) / 10,
            });
            updated++;
          } else {
            unchanged++;
          }
        } catch (err) {
          errors++;
          console.error(
            `Recalculate failed for ${member.firstName} ${member.lastName}:`,
            err
          );
        }
      }

      setResult({
        updated,
        unchanged,
        skipped,
        errors,
        changes,
        total: members.length,
      });

      if (updated > 0) {
        showMessage(
          `Recalculated: ${updated} donor${
            updated !== 1 ? "s" : ""
          } updated, ${unchanged} unchanged${
            skipped > 0
              ? `, ${skipped} prospect${skipped !== 1 ? "s" : ""} skipped`
              : ""
          }`
        );
      } else {
        showMessage(
          "All pathways already match current scoring — no changes needed"
        );
      }
    } catch (err) {
      console.error("Recalculate failed:", err);
      showMessage("Error recalculating scores", "error");
    }

    setRunning(false);
  };

  const getPathwayLabel = (key) => {
    const k =
      typeof key === "object" && key !== null
        ? key.key || key.id || String(key)
        : String(key || "");
    const pw = pathways.find((p) => p.id === k);
    if (pw) return pw.name;
    if (k === "unengaged") return "Cold";
    if (!k) return "Unknown";
    return k.charAt(0).toUpperCase() + k.slice(1);
  };

  return (
    <Card style={{ marginTop: 16 }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <div>
          <h4
            style={{
              margin: 0,
              fontSize: 15,
              fontWeight: 700,
              color: palette.text1,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <RefreshCw size={ICON_SIZES.sm} style={{ color: palette.accent1 }} />
            Recalculate All Scores
          </h4>
          <p
            style={{
              margin: "4px 0 0",
              fontSize: FONT_SIZES.xs,
              color: palette.text3,
              lineHeight: 1.4,
            }}
          >
            Recalculates every donor's engagement score using the current point
            values and updates their pathway assignment. Prospects are skipped.
            Use after changing scoring rules above.
          </p>
        </div>

        <button
          onClick={handleRecalculate}
          disabled={running}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "9px 18px",
            borderRadius: 8,
            border: "none",
            background: running ? "#d1d5db" : palette.accent1,
            color: "#fff",
            cursor: running ? "not-allowed" : "pointer",
            fontFamily: FONT_FAMILY,
            fontSize: 13,
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          <RefreshCw
            size={ICON_SIZES.sm}
            style={{
              animation: running ? "spin 1s linear infinite" : "none",
            }}
          />
          {running ? "Recalculating..." : "Recalculate Now"}
        </button>
      </div>

      {/* Results */}
      {result && (
        <div
          style={{
            marginTop: 12,
            padding: 14,
            borderRadius: 8,
            background: result.updated > 0 ? `${palette.accent1}08` : "#f0fdf4",
            border: `1px solid ${
              result.updated > 0 ? `${palette.accent1}20` : "#bbf7d0"
            }`,
          }}
        >
          {/* Summary line */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 13,
              fontWeight: 600,
              color: palette.text1,
              marginBottom: result.changes?.length > 0 ? 10 : 0,
            }}
          >
            {result.updated > 0 ? (
              <AlertCircle size={ICON_SIZES.sm} style={{ color: palette.accent1 }} />
            ) : (
              <CheckCircle size={ICON_SIZES.sm} style={{ color: "#16a34a" }} />
            )}
            {result.updated > 0
              ? `${result.updated} of ${result.total} donors moved to new pathways`
              : `All ${result.total} donors already in correct pathways`}
            {result.skipped > 0 && (
              <span
                style={{ color: palette.text3, fontSize: 12, fontWeight: 500 }}
              >
                ({result.skipped} prospect{result.skipped !== 1 ? "s" : ""}{" "}
                skipped)
              </span>
            )}
            {result.errors > 0 && (
              <span style={{ color: "#dc2626", fontSize: 12 }}>
                ({result.errors} error{result.errors !== 1 ? "s" : ""})
              </span>
            )}
          </div>

          {/* Change log table */}
          {result.changes && result.changes.length > 0 && (
            <div
              style={{
                maxHeight: 200,
                overflowY: "auto",
                fontSize: 12,
                color: palette.text2,
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontFamily: FONT_FAMILY,
                }}
              >
                <thead>
                  <tr
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      color: palette.text3,
                    }}
                  >
                    <td style={{ padding: "4px 8px 6px 0" }}>Donor</td>
                    <td style={{ padding: "4px 8px 6px" }}>Score</td>
                    <td style={{ padding: "4px 8px 6px" }}>From</td>
                    <td style={{ padding: "4px 8px 6px" }}>To</td>
                  </tr>
                </thead>
                <tbody>
                  {result.changes.map((c, i) => (
                    <tr
                      key={i}
                      style={{
                        borderTop: `1px solid ${palette.cardBorder}30`,
                      }}
                    >
                      <td
                        style={{
                          padding: "5px 8px 5px 0",
                          fontWeight: 500,
                          color: palette.text1,
                        }}
                      >
                        {c.name}
                      </td>
                      <td
                        style={{
                          padding: "5px 8px",
                          fontWeight: 600,
                          color: palette.accent1,
                        }}
                      >
                        {c.score}
                      </td>
                      <td
                        style={{
                          padding: "5px 8px",
                          color: palette.text3,
                        }}
                      >
                        {getPathwayLabel(c.from)}
                      </td>
                      <td
                        style={{
                          padding: "5px 8px",
                          fontWeight: 600,
                          color: palette.text1,
                        }}
                      >
                        → {getPathwayLabel(c.to)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};
